import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { dsgvoAPI } from '../services/dsgvoApi';
import { useToast } from '../context/ToastContext';

interface DeletionStatusModalProps {
  open: boolean;
  onClose: () => void;
}

interface DeletionStatus {
  deletion_requested: boolean;
  deletion_requested_at?: string;
  deletion_scheduled_for?: string;
  days_remaining?: number;
}

const DeletionStatusModal: React.FC<DeletionStatusModalProps> = ({
  open,
  onClose,
}) => {
  const [status, setStatus] = useState<DeletionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCanceling, setIsCanceling] = useState(false);
  const { showSuccess, showError } = useToast();

  const loadDeletionStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await dsgvoAPI.getDeletionStatus();
      setStatus(response.data.data);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Fehler beim Abrufen des Löschungsstatus';
      showError(message);
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (open) {
      loadDeletionStatus();
    }
  }, [open, loadDeletionStatus]);

  const handleCancelDeletion = async () => {
    try {
      setIsCanceling(true);
      await dsgvoAPI.cancelDeletion();
      showSuccess('Löschungsanfrage erfolgreich storniert');
      onClose();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Fehler beim Stornieren der Löschung';
      showError(message);
    } finally {
      setIsCanceling(false);
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Löschungsstatus</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!status?.deletion_requested) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Löschungsstatus</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              Es gibt keine aktiven Löschungsanfragen für Ihr Konto.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const progress = status.days_remaining
    ? Math.round(((30 - status.days_remaining) / 30) * 100)
    : 100;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Löschungsstatus</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning" icon={<CheckCircleIcon />}>
            <strong>Löschungsanfrage aktiv</strong>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Ihre Daten werden in {status.days_remaining} Tagen gelöscht.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Fortschritt:
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
            <Typography variant="caption" color="textSecondary">
              {progress}% abgelaufen ({progress}/30 Tage)
            </Typography>
          </Box>

          <Typography variant="body2" color="textSecondary">
            <strong>Beantragt am:</strong>{' '}
            {status.deletion_requested_at
              ? new Date(status.deletion_requested_at).toLocaleDateString('de-DE')
              : 'N/A'}
          </Typography>

          <Typography variant="body2" color="textSecondary">
            <strong>Löschung geplant für:</strong>{' '}
            {status.deletion_scheduled_for
              ? new Date(status.deletion_scheduled_for).toLocaleDateString('de-DE')
              : 'N/A'}
          </Typography>

          <Alert severity="info">
            Sie können diese Anfrage bis zur Löschung stornieren, falls Sie Ihre Meinung
            ändern.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCanceling}>
          Schließen
        </Button>
        <Button
          onClick={handleCancelDeletion}
          variant="contained"
          color="warning"
          disabled={isCanceling}
        >
          {isCanceling ? 'Wird storniert...' : 'Stornieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletionStatusModal;
